import { DestroyRef, inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { filter, Observable, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SlownikiApi } from './api/slowniki.api';
import { DokumentApi } from './api/dokument.api';
import { Application } from '../models/application';
import { CheckPossibilityToSubmitDokument } from '../models/check-possibility-to-submit-dokument';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationCode } from '../enums/application_code';
import { SlownikRodzaj } from '../enums/slownik-rodzaj.enum';
import { Slownik } from '../models/slownik';
import { WyznaczAdresataResponse } from '../models/wyznacz-adresata-response';
import { InfoDialog } from '../components/info-dialog/info-dialog';
import { UtworzDokumentDto } from '../models/utworz-dokument-dto';
import { environment } from '../../environments/environment';
import { ApplicationChooseJednostkaDialog } from '../../pages/application-cards/components/application-choose-jednostka/application-choose-jednostka-dialog';
import { SprawdzMozliwoscZlozeniaWnioskuError } from '../models/custom-types';
import { APP_PATHS } from '../../core/config/app-paths';
import { ApplicationContextService } from '../../core/services/application-context.service';
import {
  HttpResponseErrorSource,
  HttpResponseService,
} from '../../core/services/http-error-response.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #dialogService = inject(DialogService);
  readonly #slownikiApi = inject(SlownikiApi);
  readonly #dokumentApi = inject(DokumentApi);
  readonly #applicationContextService = inject(ApplicationContextService);

  public initApplicationFlowWithIdKorelacji(
    application: Application,
    idKorelacji: number,
  ): Observable<any> {
    return this.#initApplicationFlow(application, idKorelacji);
  }

  public initApplicationFlowWithUnit(
    application: Application,
  ): Observable<any> {
    return this.#initApplicationFlow(application);
  }

  #initApplicationFlow(
    application: Application,
    idKorelacji?: number,
  ): Observable<any> {
    return this.#dokumentApi
      .checkPossibilityToSubmitDokument$(application.kod!)
      .pipe(
        tap((resp: CheckPossibilityToSubmitDokument) => {
          const errors = this.#checkIfCanLaunchApplicationErrors(
            resp,
            application.kod!,
          );
          if (errors) {
            this.#openInfoDialog(
              errors.title,
              errors.content,
              application,
              errors.actionOnClose,
              true,
            );
          } else {
            this.#openApplication(application, idKorelacji);
          }
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby otwarcia wniosku',
          );
          return err;
        }),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  #openApplication(application: Application, idKorelacji?: number): void {
    const header = 'wnioski.' + application.kod!.toLowerCase() + '.title';
    const content = 'wnioski.' + application.kod!.toLowerCase() + '.content';
    this.#openInfoDialog(header, content, application)
      .onClose.pipe(
        filter(Boolean),
        tap(() => this.#selectUnit(application, idKorelacji)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #selectUnit(application: Application, idKorelacji?: number): void {
    if (application.kod === ApplicationCode.ZS_6) {
      this.#launchApplicationWithIdKolrecji(application, idKorelacji!);
      return;
    } else if (application.isFromZus()) {
      this.#launchApplicationWithUnit(application, '1000000000_KL_1', '6');
    } else if (application.kod === ApplicationCode.RKZ_5) {
      this.#launchApplicationWithUnit(application, '1000000000_KL_1', '6');
    } else {
      this.#slownikiApi
        .dajElementBy$({
          kodSlownika: SlownikRodzaj.SL_OBSZARY_ZS,
          filtrowanieSlownika: {
            kodFiltru: this.#adjustGroupToWniosek(application),
            param1Filtru: '',
            param2Filtru: 'KODMPIPS',
          },
          sortujWg: 'Nazwa',
        })
        .pipe(
          switchMap((response: Slownik) => {
            const kodZsPi = response.kod!;
            return this.#openChooseJednostkaDialog(
              application,
              kodZsPi,
            ).onClose.pipe(
              filter(Boolean),
              tap((idJednostki: WyznaczAdresataResponse) =>
                this.#launchApplicationWithUnit(
                  application,
                  idJednostki.kod,
                  kodZsPi,
                ),
              ),
            );
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #openInfoDialog(
    header: string,
    content: string,
    application: Application,
    actionOnClose?: () => any,
    hideSubmitButton?: boolean,
  ): DynamicDialogRef {
    const ref = this.#dialogService.open(InfoDialog, {
      closable: true,
      showHeader: true,
      header: 'Informacyjne okno dialogowe',
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      width: '55vw',
      styleClass: 'sr-only-header',
      inputValues: {
        ['header']: header,
        ['content']: content,
        ['hideSubmitButton']: hideSubmitButton,
        ['kodWniosku']: application?.kod!,
      },
      breakpoints: {
        '960px': '90vw',
        '640px': '90vw',
      },
    })!;
    if (actionOnClose) {
      ref.onClose
        .pipe(
          tap(() => actionOnClose()),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }

    return ref;
  }

  #launchApplicationWithIdKolrecji(
    application: Application,
    idKorelacji: number,
  ) {
    this.#launchApplication(application, undefined, undefined, idKorelacji);
  }

  #launchApplicationWithUnit(
    application: Application,
    idJednostki: string,
    kodZsPi: string,
  ) {
    this.#launchApplication(application, idJednostki, kodZsPi);
  }

  #launchApplication(
    application: Application,
    idJednostki?: string,
    kodZsPi?: string,
    idKorelacji?: number,
  ): void {
    this.#dokumentApi
      .checkPossibilityToSubmitDokument$(application.kod!)
      .pipe(
        switchMap((status: CheckPossibilityToSubmitDokument) => {
          if (status.czyWniosekDostepny) {
            return idKorelacji
              ? this.#createDocumentWithIdKorelacji(idKorelacji!)
              : this.#createDocument(application, idJednostki!, kodZsPi!);
          } else {
            return throwError(
              () =>
                new Error(
                  'Wniosek jest obecnie niedostępny dla wybranego typu dokumentu.',
                ),
            );
          }
        }),
        tap((response: UtworzDokumentDto) => {
          const baseUrl = environment.KREATORY_FE;
          const appMode = this.#getApplicationMode(application.kod!);
          const appCode =
            application.kod!.replace('_', '').toLowerCase() + appMode;
          const { idJednostki, id } = response;

          window.location.href = `${baseUrl}/${appCode}/${idJednostki}/${id}`;
        }),
        catchError((err: HttpErrorResponse) => {
          this.#handleCreateNewDokumentError(err);
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #openChooseJednostkaDialog(
    application: Application,
    obszarZSkod: string,
  ): DynamicDialogRef {
    const header: string =
      'Wybierz urząd/instytucję, do którego składasz wniosek';

    return this.#dialogService.open(ApplicationChooseJednostkaDialog, {
      closable: true,
      closeOnEscape: true,
      header: header,
      modal: true,
      dismissableMask: true,
      width: '50vw',
      inputValues: {
        application: application,
        obszarZSkod: obszarZSkod,
      },
      breakpoints: {
        '1400px': '80vw',
      },
    })!;
  }

  #checkIfCanLaunchApplicationErrors(
    resp: CheckPossibilityToSubmitDokument,
    applicationCode: ApplicationCode,
  ): SprawdzMozliwoscZlozeniaWnioskuError | null {
    const code = applicationCode.toLowerCase();
    const wniosekError = this.#buildError(
      `wnioski.${code}.title`,
      `wnioski.${code}.contentDisabled`,
    );

    if (!resp.czyWniosekDostepny) {
      return wniosekError;
    } else if (!resp.emailOk) {
      return this.#buildError(
        'czyMozliwoscWyslaniaWniosku.wymaganaWeryfikacjaAdresuEmail.title',
        'czyMozliwoscWyslaniaWniosku.wymaganaWeryfikacjaAdresuEmail.content',
        () => {
          if (this.#applicationContextService.isOrganizationContext()) {
            this.#applicationContextService.switchToBeneficiary(false);
          }
          const url = `${APP_PATHS.userSettings.root}`;
          this.#router.navigate([url]);
        },
      );
    } else if (!resp.pelnomocnictwoOk) {
      return this.#buildError(
        'czyMozliwoscWyslaniaWniosku.wymaganeDodaniePelnomocnictwa.title',
        'czyMozliwoscWyslaniaWniosku.wymaganeDodaniePelnomocnictwa.content',
      );
    } else if (
      resp.czyPelnomocnik &&
      [ApplicationCode.RKZ_1, ApplicationCode.RKZ_3].includes(applicationCode)
    ) {
      return this.#buildError(
        'czyMozliwoscWyslaniaWniosku.niewystarczajaceUprawnienia.title',
        'czyMozliwoscWyslaniaWniosku.niewystarczajaceUprawnienia.content',
      );
    } else if (
      resp.czyPodmiotGmina &&
      [ApplicationCode.RKZ_6].includes(applicationCode)
    ) {
      return this.#buildError(
        'czyMozliwoscWyslaniaWniosku.informacjaRKZ6.title',
        'czyMozliwoscWyslaniaWniosku.informacjaRKZ6.content',
      );
    } else if (
      resp.czyPodmiotGmina &&
      [ApplicationCode.RKZ_7].includes(applicationCode)
    ) {
      return this.#buildError(
        'czyMozliwoscWyslaniaWniosku.informacjaRKZ7.title',
        'czyMozliwoscWyslaniaWniosku.informacjaRKZ7.content',
      );
    }
    return null;
  }

  #buildError(
    title: string,
    content: string,
    actionOnClose?: () => any,
  ): SprawdzMozliwoscZlozeniaWnioskuError {
    return { title, content, actionOnClose };
  }

  #adjustGroupToWniosek(application: Application): string {
    switch (application.kod) {
      case ApplicationCode.PPW:
        return 'ON';
      default:
        return application.obszar!;
    }
  }

  #handleCreateNewDokumentError(err: HttpErrorResponse): void {
    this.#httpResponseService.showErrorMessage(
      err,
      'Wystąpił błąd podczas próby utworzenia wniosku',
      HttpResponseErrorSource.MESSAGE_FROM_ERROR_CODE,
    );
  }

  /**
   * Return application mode - additional letter in appCode
   * @param applicationCode document type
   * @returns {string} wizard mode: o - organizacja, b - beneficjent (tylko RKZ)
   */
  #getApplicationMode(applicationCode: ApplicationCode): string {
    if (
      [
        ApplicationCode.RKZ_1,
        ApplicationCode.RKZ_2,
        ApplicationCode.RKZ_6,
        ApplicationCode.RKZ_7,
      ].includes(applicationCode)
    ) {
      return this.#applicationContextService.isOrganizationContext()
        ? 'o'
        : 'b';
    } else if (
      [
        ApplicationCode.RKZ_3,
        ApplicationCode.RKZ_4,
        ApplicationCode.RKZ_8,
      ].includes(applicationCode)
    ) {
      return 'o';
    }
    return '';
  }

  #createDocument(
    application: Application,
    idJednostki: string,
    kodZsPi: string,
  ) {
    return this.#dokumentApi.createNewDokument$({
      czyDokumentTymczasowy: true,
      kodZsPi: kodZsPi,
      czyLiferay: false,
      czyKorespondencja: false,
      idJednostki: idJednostki,
      kodTypuDokumentu: application.kod,
    });
  }

  #createDocumentWithIdKorelacji(idKorelacji: number) {
    return this.#dokumentApi.createNewDokument$({
      czyDokumentTymczasowy: true,
      czyLiferay: false,
      czyKorespondencja: true,
      korespondencjaWSprawie: idKorelacji,
      kodTypuDokumentu: ApplicationCode.ZS_6,
    });
  }
}
